import React from 'react'
import { createRoot } from 'react-dom/client'
import { Routes } from '@generouted/react-router'
import './index.css'
import '@/i18n.config'
import { loadConfig } from '@/infra/env/getEnv'
import ConfigError from '@/src/components/config-error/ConfigError'
import { createOidcAuthenticationAdapter } from '@/infra/auth/OidcAuthenticationAdapter'
import { authPort, setAuthPort, setUserPreferencesPort } from '@/infra/config/beans'
import { createHttpClient, setHttpClient } from '@/infra/http/httpClient'
import { zustandUserPreferencesAdapter } from '@/infra/user-preferences/zustandUserPreferenceAdapter'
import { addReactError } from '@datadog/browser-rum-react'
import { version } from '../package.json'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { initializeDatadogServices, setDatadogUser } from '@/datadog-initialization.config'
import {
  identifyUser,
  initAmplitude,
  trackAndFlushEvent,
  trackEvent,
} from '@/src/analytics/amplitude'
import { FeatureFlagProvider } from '@/src/context/FeatureFlagContext'
import {
  getBootstrapPhase,
  installClientErrorMonitoring,
  safeDatadogError,
  setBootstrapPhase,
  toSerializable,
} from '@/infra/monitoring/clientErrorMonitoring'

installClientErrorMonitoring()

const root = createRoot(document.getElementById('root')!, {
  onUncaughtError: (error: any, errorInfo) => {
    addReactError(error, errorInfo)
  },
  onCaughtError: (error: any, errorInfo) => {
    addReactError(error, errorInfo)
  },
  onRecoverableError: (error: any, errorInfo) => {
    addReactError(error, errorInfo)
  },
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

try {
  setBootstrapPhase('config-load')
  const cfg = await loadConfig()

  setBootstrapPhase('adapter-init')
  setAuthPort(createOidcAuthenticationAdapter(cfg))
  setHttpClient(createHttpClient(cfg))
  setUserPreferencesPort(zustandUserPreferencesAdapter)

  setBootstrapPhase('datadog-init')
  initializeDatadogServices(cfg, version)
  initAmplitude(cfg.AMPLITUDE_API_KEY, cfg.ENVIRONMENT)

  setBootstrapPhase('auth-current-user')
  const user = await authPort.getCurrentUser()
  if (user) {
    setDatadogUser(user, cfg)
    identifyUser(user)
    trackEvent('APP_OPENED')
    globalThis.addEventListener('pagehide', () => trackAndFlushEvent('APP_CLOSED'))
    globalThis.addEventListener('popstate', () => trackEvent('BROWSER_BACK_BUTTON_CLICK'))
  }

  setBootstrapPhase('render')
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <FeatureFlagProvider>
          {cfg.ENVIRONMENT === 'local' && <ReactQueryDevtools initialIsOpen={false} />}
          <Routes />
        </FeatureFlagProvider>
      </QueryClientProvider>
    </React.StrictMode>
  )
} catch (e) {
  const phase = getBootstrapPhase()
  const datadogError = e instanceof Error ? e : undefined
  console.error('[bootstrap:error]', { phase, error: e })
  safeDatadogError('bootstrap_error', { phase, error: toSerializable(e) }, datadogError)
  root.render(<ConfigError error={e} />)
}
